/**
 * @component ProducerOnboarding (SipLogic Waitlist)
 * @version 1.1.0
 */

import { useState, useEffect, useRef, CSSProperties } from "react";
import ExpertPage from "./ExpertPage";
import AuthPage from "./AuthPage";
import { submitProducerWaitlist } from './supabaseClient';
import ProducerDashboard from "./ProducerDashboard";
import ExpertDashboard from "./ExpertDashboard";

const T = {
  burgundy: "#4A0E0E",
  burgundyMid: "#6B1A1A",
  burgundyLight: "#8B2A2A",
  charcoal: "#1C1410",
  charcoalMid: "#2A1E18",
  charcoalLight: "#3A2A22",
  cream: "#F5F0E8",
  creamMuted: "#C8B89A",
  copper: "#8B6B4A",
  copperLight: "#B08A60",
  ink: "#0A0806",
  surface: "#120E0A",
  surfaceRaised: "#1A1410",
  complianceBlue: "#0A0A1A",
  complianceBorder: "#2A2A5A",
  complianceText: "#7070B0",
} as const;

const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=Crimson+Pro:ital,wght@0,200;0,300;0,400;1,200;1,300&family=Josefin+Sans:wght@100;200;300&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  html,
  body,
  #root {
    width: 100%;
    max-width: 100%;
    min-height: 100%;
    overflow-x: hidden;
  }

  html {
    scroll-behavior: smooth;
  }

  body {
    position: relative;
    background: #0A0806;
    color: #F5F0E8;
    font-family: 'Crimson Pro', Georgia, serif;
    -webkit-font-smoothing: antialiased;
    overflow-x: hidden;
  }

  #root {
    overflow-x: clip;
  }

  * {
    min-width: 0;
  }

  img,
  video,
  canvas,
  svg {
    max-width: 100%;
  }

  ::-webkit-scrollbar { width: 3px; }
  ::-webkit-scrollbar-track { background: #0A0806; }
  ::-webkit-scrollbar-thumb { background: #4A0E0E; }
  @keyframes fadeUp { from { opacity: 0; transform: translateY(22px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes expandIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes drawLine { from { width: 0; opacity: 0; } to { width: 80px; opacity: 1; } }
  @keyframes pulse { 0%, 100% { opacity: 0.35; } 50% { opacity: 1; } }
  @keyframes shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
  @keyframes cardShine { from { left: -100%; } to { left: 200%; } }
  .sl-nav-link { position: relative; font-family: 'Josefin Sans', sans-serif; font-weight: 200; font-size: 10px; letter-spacing: 0.35em; text-transform: uppercase; color: #8B6B4A; cursor: pointer; padding: 6px 0; background: none; border: none; transition: color 0.4s ease; }
  .sl-nav-link::after { content: ''; position: absolute; bottom: 0; left: 0; width: 0; height: 1px; background: #B08A60; transition: width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94); }
  .sl-nav-link:hover { color: #C8B89A; }
  .sl-nav-link:hover::after { width: 100%; }
  .sl-tier-card { position: relative; background: #120E0A; border: 1px solid #251810; border-radius: 1px; padding: 26px 24px; cursor: pointer; overflow: hidden; transition: background 0.35s ease, border-color 0.35s ease, transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.35s ease; }
  .sl-tier-card::before { content: ''; position: absolute; top: 0; left: -100%; width: 60%; height: 100%; background: linear-gradient(90deg, transparent, rgba(139,107,74,0.07), transparent); }
  .sl-tier-card:hover::before { animation: cardShine 0.7s ease forwards; }
  .sl-tier-card:hover { background: #1A1410; border-color: #3A2018; transform: translateY(-2px); box-shadow: 0 14px 44px rgba(0,0,0,0.55); }
  .sl-tier-card.selected { background: #1E0E0A; border-color: #6B1A1A; transform: translateY(-1px); box-shadow: 0 8px 32px rgba(74,14,14,0.35); }
  .sl-tier-card .card-accent { position: absolute; bottom: 0; left: 0; right: 0; height: 1px; background: transparent; transition: background 0.4s ease; }
  .sl-tier-card.selected .card-accent { background: linear-gradient(90deg, transparent, #6B1A1A, transparent); }
  .sl-input { width: 100%; background: transparent; border: none; border-bottom: 1px solid #2A1A14; color: #F5F0E8; font-family: 'Crimson Pro', Georgia, serif; font-size: 16px; font-weight: 300; padding: 10px 0; outline: none; caret-color: #B08A60; transition: border-color 0.35s ease; }
  .sl-input::placeholder { color: #4A3020; font-style: italic; }
  .sl-input:focus { border-bottom-color: #8B6B4A; }
  .sl-input.has-error { border-bottom-color: #8B2A2A; }
  .sl-select { width: 100%; background: #120E0A; border: none; border-bottom: 1px solid #2A1A14; color: #F5F0E8; font-family: 'Josefin Sans', sans-serif; font-size: 11px; font-weight: 200; letter-spacing: 0.15em; padding: 10px 0; outline: none; cursor: pointer; appearance: none; transition: border-color 0.35s ease; }
  .sl-select:focus { border-bottom-color: #8B6B4A; }
  .sl-select option { background: #1A1008; }
  .sl-cta { position: relative; overflow: hidden; width: 100%; background: #4A0E0E; border: 1px solid #6B1A1A; color: #F5F0E8; font-family: 'Josefin Sans', sans-serif; font-weight: 300; font-size: 11px; letter-spacing: 0.4em; text-transform: uppercase; padding: 17px 48px; cursor: pointer; transition: background 0.4s ease, border-color 0.4s ease, transform 0.2s ease; }
  .sl-cta::before { content: ''; position: absolute; top: 0; left: -100%; width: 100%; height: 100%; background: linear-gradient(90deg, transparent, rgba(255,255,255,0.07), transparent); transition: left 0.55s ease; }
  .sl-cta:hover::before { left: 100%; }
  .sl-cta:hover { background: #6B1A1A; border-color: #8B2A2A; transform: translateY(-1px); }
  .sl-cta:active { transform: translateY(0px); }
  .sl-cta:disabled { opacity: 0.38; cursor: not-allowed; transform: none; }
  .sl-shimmer { background: linear-gradient(90deg, #C8B89A 0%, #F5F0E8 40%, #B08A60 60%, #C8B89A 100%); background-size: 200% auto; -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; animation: shimmer 5s linear infinite; }
  .anim-fade-up { animation: fadeUp 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94) both; }
  .anim-fade-in { animation: fadeIn 0.6s ease both; }
  .anim-expand { animation: expandIn 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) both; }

  @media (max-width: 768px) {
    .sl-home-page,
    .sl-home-nav,
    .sl-home-hero,
    .sl-section,
    footer {
      width: 100% !important;
      max-width: 100vw !important;
      overflow-x: hidden !important;
    }

    .sl-home-nav-inner,
    .sl-home-hero > *,
    .sl-section > * {
      max-width: 100% !important;
    }

    .sl-home-hero h1,
    .sl-home-hero p {
      overflow-wrap: break-word !important;
    }

    .sl-home-nav {
      padding: 0 16px !important;
    }

    .sl-home-nav-inner {
      height: auto !important;
      min-height: 64px !important;
      gap: 12px !important;
    }

    .sl-home-nav-links {
      display: none !important;
    }

    .sl-home-hero {
      min-height: 100svh !important;
      padding: 96px 16px 56px !important;
      justify-content: center !important;
    }

    .sl-home-hero h1 {
      font-size: 42px !important;
      line-height: 0.95 !important;
    }

    .sl-home-hero p {
      font-size: 16px !important;
      line-height: 1.7 !important;
    }

    .sl-home-proof {
      flex-direction: column !important;
      gap: 22px !important;
      margin-top: 40px !important;
    }

    .sl-home-actions {
      flex-direction: column !important;
      width: 100% !important;
      gap: 12px !important;
    }

    .sl-home-actions button {
      width: 100% !important;
    }

    .sl-section {
      padding: 64px 16px !important;
    }

    .sl-form-grid {
      grid-template-columns: 1fr !important;
      gap: 24px !important;
    }
  }

  @media (max-width: 430px) {
    .sl-home-hero h1 {
      font-size: 36px !important;
    }
  }

`;

type TierID = "Producer" | "Broker" | "Wholesaler";
interface Tier { id: TierID; icon: string; label: string; sub: string; pitch: string; compliance: boolean; }

const TIERS: Tier[] = [
  { id: "Producer", icon: "◈", label: "Producer", sub: "Winery or Distillery", pitch: "You create the liquid. We find the credentialed voice to represent it at retail.", compliance: false },
  { id: "Broker", icon: "◇", label: "Broker", sub: "Brand Importer or Agent", pitch: "You represent a portfolio. We match each SKU to the right certified specialist.", compliance: false },
  { id: "Wholesaler", icon: "◉", label: "Wholesaler", sub: "Distributor", pitch: "You move volume. We ensure GA DOR compliance on every retailer-facing funded event.", compliance: true },
];

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

function OrnamentRule({ glyph = "◈" }: { glyph?: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
      <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg, transparent, #3A2A22)" }} />
      <span style={{ color: "#8B6B4A", fontSize: 12, opacity: 0.6 }}>{glyph}</span>
      <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg, #3A2A22, transparent)" }} />
    </div>
  );
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ fontFamily: "'Josefin Sans', sans-serif", fontWeight: 100, fontSize: 9, letterSpacing: "0.5em", textTransform: "uppercase", color: "#8B6B4A", marginBottom: 16 }}>
      {children}
    </p>
  );
}

function ComplianceNotice() {
  return (
    <div className="anim-fade-in" style={{ padding: "18px 22px", marginBottom: 32, background: "#0A0A1A", border: "1px solid #2A2A5A" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <span style={{ color: "#7070B0", fontSize: 14 }}>◈</span>
        <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontWeight: 200, fontSize: 9, letterSpacing: "0.35em", textTransform: "uppercase", color: "#7070B0" }}>GA DOR Title 3 — Compliance Notice</span>
      </div>
      <p style={{ fontFamily: "'Crimson Pro', serif", fontSize: 15, color: "#6060A0", fontStyle: "italic", lineHeight: 1.75, marginBottom: 14 }}>
        Under Georgia's three-tier system, a licensed wholesaler may not provide "things of value" to a retailer. Before posting any funded tasting event, you must confirm the event is structured as a <strong style={{ fontStyle: "normal", color: "#8080C0" }}>brand-funded educational activity</strong>.
      </p>
      <div style={{ borderTop: "1px solid #2A2A5A", paddingTop: 12 }}>
        {["Retailer holds a valid Georgia pouring permit","All pourers are 21+ and certified by SipLogic","Event follows GA volume limits (1.5 oz spirits · 3 oz wine per taste)","No bottle sales will occur at this tasting","Event has been pre-approved by retailer management"].map((item, i) => (
          <div key={i} style={{ display: "flex", gap: 10, marginBottom: 6 }}>
            <span style={{ color: "#7070B0", fontSize: 10, marginTop: 2, flexShrink: 0 }}>○</span>
            <span style={{ fontFamily: "'Crimson Pro', serif", fontSize: 13, color: "#5050A0", fontStyle: "italic", lineHeight: 1.5 }}>{item}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

interface FormData { company: string; contact: string; email: string; phone: string; state: string; }
type FormErrors = Partial<Record<keyof FormData, string>>;

function NavCTAButton() {
  const [hovered, setHovered] = useState(false);
  return (
    <button onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} style={{ fontFamily: "'Josefin Sans', sans-serif", fontWeight: 200, fontSize: 9, letterSpacing: "0.3em", textTransform: "uppercase", color: "#F5F0E8", background: hovered ? "#4A0E0E" : "transparent", border: `1px solid ${hovered ? "#8B2A2A" : "#6B1A1A"}`, padding: "8px 20px", cursor: "pointer", transition: "all 0.35s ease" }}>
      Request Access
    </button>
  );
}

function ChangeButton({ onClick }: { onClick: () => void }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "'Josefin Sans', sans-serif", fontWeight: 200, fontSize: 9, letterSpacing: "0.3em", textTransform: "uppercase", color: hovered ? "#F5F0E8" : "#8B6B4A", transition: "color 0.2s ease" }}>
      Change →
    </button>
  );
}

export default function App(): JSX.Element {
  if (window.location.pathname.startsWith("/experts")) return <ExpertPage />;
  if (window.location.pathname.startsWith("/login")) return <AuthPage />;
  if (window.location.pathname.startsWith("/signup")) return <AuthPage />;
  if (window.location.pathname.startsWith("/producer-dashboard")) return <ProducerDashboard />;
  if (window.location.pathname.startsWith("/expert-dashboard")) return <ExpertDashboard />;

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedTier, setSelectedTier] = useState<TierID | null>(null);
  const [form, setForm] = useState<FormData>({ company: "", contact: "", email: "", phone: "", state: "" });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [navScrolled, setNavScrolled] = useState(false);
  const formRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const tag = document.createElement("style");
    tag.innerHTML = GLOBAL_CSS;
    document.head.appendChild(tag);
    return () => { document.head.removeChild(tag); };
  }, []);

  useEffect(() => {
    const handler = () => setNavScrolled(window.scrollY > 24);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const validate = (): FormErrors => {
    const e: FormErrors = {};
    if (!form.company.trim()) e.company = "Required";
    if (!form.contact.trim()) e.contact = "Required";
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = "Valid email required";
    if (!form.state) e.state = "Required";
    return e;
  };

  const onChange = (key: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    if (errors[key]) setErrors((prev) => { const n = { ...prev }; delete n[key]; return n; });
  };

  const handleTierSelect = (id: TierID) => {
    setSelectedTier(id);
    setTimeout(() => {
      setStep(2);
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 280);
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSubmitting(true);
    try {
      await submitProducerWaitlist({
        company_name: form.company,
        contact_name: form.contact,
        email: form.email,
        phone: form.phone,
        state: form.state,
        tier: selectedTier || '',
      });
      setStep(3);
    } catch (err) {
      console.error(err);
      setStep(3);
    } finally {
      setSubmitting(false);
    }
  };

  const labelStyle = (hasError?: boolean): CSSProperties => ({
    display: "block", fontFamily: "'Josefin Sans', sans-serif", fontWeight: 100, fontSize: 9,
    letterSpacing: "0.4em", textTransform: "uppercase", color: hasError ? T.burgundyLight : T.copper, marginBottom: 8,
  });

  return (
    <div className="sl-home-page" style={{ minHeight: "100vh", background: T.ink, width: "100%", maxWidth: "100vw", overflowX: "hidden" }}>

      <nav className="sl-home-nav" style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 100, padding: "0 48px", background: navScrolled ? "linear-gradient(180deg,rgba(10,6,4,0.97),rgba(10,6,4,0.92))" : "linear-gradient(180deg,rgba(10,6,4,0.75),transparent)", backdropFilter: navScrolled ? "blur(14px)" : "none", borderBottom: `1px solid ${navScrolled ? "rgba(74,14,14,0.35)" : "transparent"}`, transition: "background 0.55s ease" }}>
        <div className="sl-home-nav-inner" style={{ maxWidth: 1100, margin: "0 auto", height: 72, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12, cursor: "pointer" }}>
            <span style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: 23, color: T.cream, letterSpacing: "0.08em" }}>SipLogic</span>
            <span style={{ width: 1, height: 14, background: T.burgundyMid, display: "inline-block" }} />
            <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontWeight: 100, fontSize: 9, color: T.copper, letterSpacing: "0.3em", textTransform: "uppercase" }}>Georgia</span>
          </div>
          <div className="sl-home-nav-links" style={{ display: "flex", gap: 36, alignItems: "center" }}>
            {(["Join Waitlist", "How It Works"] as const).map((label) => (
              <button key={label} className="sl-nav-link">{label}</button>
            ))}
            <button className="sl-nav-link" onClick={() => window.location.href = '/experts'}>For Experts</button>
            <NavCTAButton />
          </div>
        </div>
      </nav>

      <section
        className="sl-home-hero"
        style={{
          minHeight: "100vh",
          width: "100%",
          maxWidth: "100vw",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          padding: "120px 24px 80px",
          position: "relative",
          background: `radial-gradient(ellipse 90% 70% at 50% 30%, #1A0808 0%, ${T.ink} 70%)`,
          textAlign: "center",
        }}
      >
        {[600, 420, 260].map((size) => (
          <div
            key={size}
            style={{
              position: "absolute",
              top: "35%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: `min(${size}px, 92vw)`,
              height: `min(${size}px, 92vw)`,
              borderRadius: "50%",
              border: `1px solid rgba(74,14,14,${0.05 + (600 - size) / 3000})`,
              pointerEvents: "none",
            }}
          />
        ))}
        <div className="anim-fade-up" style={{ animationDelay: "0.1s", marginBottom: 24 }}>
          <OrnamentRule />
          <div style={{ marginTop: 16 }}><Eyebrow>Georgia's Elite SME Tasting Marketplace</Eyebrow></div>
        </div>
        <h1 className="anim-fade-up" style={{ animationDelay: "0.25s", fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "clamp(44px, 7.5vw, 82px)", color: T.cream, lineHeight: 1.1, letterSpacing: "0.02em", maxWidth: 800 }}>
          Your Bottle.{" "}<em className="sl-shimmer" style={{ fontStyle: "italic" }}>Their Expertise.</em>
        </h1>
        <p className="anim-fade-up" style={{ animationDelay: "0.42s", fontFamily: "'Crimson Pro', serif", fontWeight: 300, fontSize: 20, color: T.copper, maxWidth: 510, marginTop: 24, lineHeight: 1.75, fontStyle: "italic" }}>
          Connect with Master Sommeliers and certified spirits professionals who turn tastings into transactions.
        </p>
        <div className="anim-fade-in sl-home-proof" style={{ animationDelay: "0.7s", display: "flex", gap: 48, marginTop: 56 }}>
          {[{ n: "Tier 1–3", label: "Certified Experts" }, { n: "GA DOR", label: "Compliant by Design" }, { n: "Stripe", label: "Milestone Payments" }].map((stat) => (
            <div key={stat.label} style={{ textAlign: "center" }}>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: 22, color: T.creamMuted, letterSpacing: "0.06em" }}>{stat.n}</div>
              <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontWeight: 100, fontSize: 8, letterSpacing: "0.35em", textTransform: "uppercase", color: T.copper, marginTop: 4 }}>{stat.label}</div>
            </div>
          ))}
        </div>
        <div className="anim-fade-in" style={{ animationDelay: "1.4s", marginTop: 72, display: "flex", flexDirection: "column", alignItems: "center", gap: 20 }}>
          <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontWeight: 100, fontSize: 9, letterSpacing: "0.4em", textTransform: "uppercase", color: T.charcoalLight }}>Get Started</span>
          <div className="sl-home-actions" style={{ display: "flex", gap: 16 }}>
            <button
              onClick={() => {
                const user = localStorage.getItem('sl_user');
                window.location.href = user ? '/producer-dashboard' : '/login';
              }}
              style={{ fontFamily: "'Josefin Sans', sans-serif", fontWeight: 200, fontSize: 9, letterSpacing: "0.35em", textTransform: "uppercase", color: T.cream, background: T.burgundy, border: `1px solid ${T.burgundyMid}`, padding: "12px 28px", cursor: "pointer", transition: "all 0.35s ease" }}
              onMouseEnter={e => (e.currentTarget.style.background = T.burgundyMid)}
              onMouseLeave={e => (e.currentTarget.style.background = T.burgundy)}
            >
              I'm a Producer
            </button>
            <button
              onClick={() => {
                const user = localStorage.getItem('sl_user');
                window.location.href = user ? '/expert-dashboard' : '/login';
              }}
              style={{ fontFamily: "'Josefin Sans', sans-serif", fontWeight: 200, fontSize: 9, letterSpacing: "0.35em", textTransform: "uppercase", color: "#C8A84B", background: "transparent", border: "1px solid #9A7A35", padding: "12px 28px", cursor: "pointer", transition: "all 0.35s ease" }}
              onMouseEnter={e => { e.currentTarget.style.background = "#9A7A35"; e.currentTarget.style.color = T.ink; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#C8A84B"; }}
            >
              I'm an Expert
            </button>
          </div>
        </div>
      </section>

      <section ref={formRef} className="sl-section" style={{ minHeight: "100vh", padding: "80px 24px 140px", background: `linear-gradient(180deg, ${T.ink} 0%, #0E0A08 100%)`, display: "flex", flexDirection: "column", alignItems: "center" }}>
        {step !== 3 ? (
          <div style={{ width: "100%", maxWidth: 620 }}>
            <div style={{ textAlign: "center", marginBottom: 56 }}>
              <Eyebrow>Step {step} of 2 — {step === 1 ? "Your Role" : "Your Details"}</Eyebrow>
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: 38, color: T.cream, letterSpacing: "0.03em", lineHeight: 1.2 }}>
                {step === 1 ? "How does your brand reach the market?" : "Tell us about your brand."}
              </h2>
              {step === 1 && (
                <p style={{ marginTop: 14, fontFamily: "'Crimson Pro', serif", fontSize: 17, color: T.copper, fontStyle: "italic", lineHeight: 1.65 }}>
                  Your funding source determines your compliance path and the experts we surface for you.
                </p>
              )}
            </div>

            {step === 1 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {TIERS.map((tier) => (
                  <div key={tier.id} className={`sl-tier-card${selectedTier === tier.id ? " selected" : ""}`} onClick={() => handleTierSelect(tier.id)}>
                    {tier.compliance && (
                      <div style={{ position: "absolute", top: 16, right: 16, fontFamily: "'Josefin Sans', sans-serif", fontWeight: 200, fontSize: 8, letterSpacing: "0.3em", textTransform: "uppercase", color: T.complianceText, border: `1px solid ${T.complianceBorder}`, padding: "2px 8px" }}>
                        GA DOR Compliance
                      </div>
                    )}
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 20 }}>
                      <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 30, lineHeight: 1, marginTop: 2, flexShrink: 0, color: selectedTier === tier.id ? T.copperLight : T.burgundyLight, transition: "color 0.3s ease" }}>{tier.icon}</span>
                      <div>
                        <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 5 }}>
                          <span style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400, fontSize: 21, color: selectedTier === tier.id ? T.cream : T.creamMuted, letterSpacing: "0.04em", transition: "color 0.3s ease" }}>{tier.label}</span>
                          <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontWeight: 100, fontSize: 9, letterSpacing: "0.25em", textTransform: "uppercase", color: T.copper }}>{tier.sub}</span>
                        </div>
                        <p style={{ fontFamily: "'Crimson Pro', serif", fontSize: 15, color: T.copper, fontStyle: "italic", lineHeight: 1.65 }}>{tier.pitch}</p>
                      </div>
                    </div>
                    <div className="card-accent" />
                  </div>
                ))}
              </div>
            )}

            {step === 2 && (
              <div className="anim-expand">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", marginBottom: 32, background: T.surface, border: `1px solid ${T.burgundy}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={{ fontSize: 20, color: T.copperLight }}>{TIERS.find((t) => t.id === selectedTier)?.icon}</span>
                    <div>
                      <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontWeight: 100, fontSize: 9, letterSpacing: "0.3em", textTransform: "uppercase", color: T.copper, marginBottom: 3 }}>Funding Source — Locked</div>
                      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, color: T.cream }}>{selectedTier}</div>
                    </div>
                  </div>
                  <ChangeButton onClick={() => { setStep(1); setSelectedTier(null); }} />
                </div>

                {selectedTier === "Wholesaler" && <ComplianceNotice />}

                <div style={{ display: "flex", flexDirection: "column", gap: 34 }}>
                  {[
                    { key: "company" as const, label: "Brand or Company Name", placeholder: "Eagle Rock Vineyards", type: "text" },
                    { key: "contact" as const, label: "Your Full Name", placeholder: "Director of Sales", type: "text" },
                    { key: "email" as const, label: "Business Email", placeholder: "name@yourbrand.com", type: "email" },
                    { key: "phone" as const, label: "Phone (optional)", placeholder: "404 · 000 · 0000", type: "tel" },
                  ].map(({ key, label, placeholder, type }) => (
                    <div key={key}>
                      <label style={labelStyle(!!errors[key])}>
                        {label}
                        {errors[key] && <span style={{ marginLeft: 10, fontStyle: "italic", fontSize: 8, color: T.burgundyLight }}>— {errors[key]}</span>}
                      </label>
                      <input className={`sl-input${errors[key] ? " has-error" : ""}`} type={type} placeholder={placeholder} value={form[key]} onChange={onChange(key)} autoComplete="off" />
                    </div>
                  ))}

                  <div>
                    <label style={labelStyle(!!errors.state)}>
                      State of Operation
                      {errors.state && <span style={{ marginLeft: 10, fontStyle: "italic", fontSize: 8, color: T.burgundyLight }}>— Required</span>}
                    </label>
                    <div style={{ position: "relative" }}>
                      <select className="sl-select" value={form.state} onChange={onChange("state")}>
                        <option value="">Select your state…</option>
                        {US_STATES.map((s) => (<option key={s} value={s}>{s}</option>))}
                      </select>
                      <span style={{ position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)", color: T.copper, fontSize: 10, pointerEvents: "none" }}>▾</span>
                    </div>
                    {form.state && form.state !== "GA" && (
                      <p className="anim-fade-in" style={{ marginTop: 10, fontFamily: "'Crimson Pro', serif", fontSize: 14, color: T.copper, fontStyle: "italic" }}>
                        SipLogic is launching in Georgia first. We'll notify you personally when we expand to {form.state}.
                      </p>
                    )}
                  </div>

                  <div style={{ marginTop: 8 }}>
                    <button className="sl-cta" onClick={handleSubmit} disabled={submitting}>
                      {submitting ? <span style={{ animation: "pulse 1s ease-in-out infinite" }}>◈ &nbsp; Securing your place…</span> : "Request Early Access"}
                    </button>
                    <p style={{ marginTop: 16, textAlign: "center", fontFamily: "'Crimson Pro', serif", fontSize: 13, color: "#3A2A1A", fontStyle: "italic" }}>
                      No commitment. We'll reach out personally to confirm your spot.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="anim-fade-up" style={{ width: "100%", maxWidth: 560, textAlign: "center" }}>
            <div style={{ fontSize: 48, color: T.burgundyLight, fontFamily: "'Cormorant Garamond', serif", marginBottom: 28, lineHeight: 1, animation: "pulse 3s ease-in-out infinite" }}>◈</div>
            <div style={{ height: 1, margin: "0 auto 40px", background: `linear-gradient(90deg, transparent, ${T.burgundyMid}, transparent)`, animation: "drawLine 1s ease 0.2s both" }} />
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: 42, color: T.cream, letterSpacing: "0.04em", lineHeight: 1.15, marginBottom: 20 }}>You're on the list.</h2>
            <p style={{ fontFamily: "'Crimson Pro', serif", fontSize: 19, color: T.copper, fontStyle: "italic", lineHeight: 1.8, maxWidth: 420, margin: "0 auto 40px" }}>
              {form.company || "Your brand"} has been added to the SipLogic waitlist. We'll be in touch personally to confirm your access and arrange your first expert match.
            </p>
            <div style={{ display: "inline-block", padding: "10px 28px", border: `1px solid ${T.charcoalLight}`, fontFamily: "'Josefin Sans', sans-serif", fontWeight: 100, fontSize: 9, letterSpacing: "0.4em", textTransform: "uppercase", color: T.copper }}>
              {selectedTier} · {form.state || "GA"} · Pending Verification
            </div>
          </div>
        )}
      </section>

      <footer style={{ borderTop: "1px solid #1A1008", padding: "28px 48px", background: T.ink, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <span style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: 18, color: T.charcoalLight, letterSpacing: "0.06em" }}>SipLogic</span>
        <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontWeight: 100, fontSize: 9, letterSpacing: "0.3em", textTransform: "uppercase", color: "#2A1A10" }}>siplogic.io · Georgia · MVP v1.0</span>
        <span style={{ fontFamily: "'Crimson Pro', serif", fontSize: 13, color: "#2A1A10", fontStyle: "italic" }}>Where expertise meets the pour.</span>
      </footer>
    </div>
  );
}